import { useState, useEffect } from 'react';
import './Carousel.css';

function Carousel({ images, autoScrollInterval = 5000 }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);

    // Auto-scroll functionality - only when lightbox is closed
    useEffect(() => {
        if (isLightboxOpen) return; // Don't auto-scroll when lightbox is open

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => 
                prevIndex === images.length - 1 ? 0 : prevIndex + 1
            );
        }, autoScrollInterval);

        return () => clearInterval(interval);
    }, [images.length, autoScrollInterval, isLightboxOpen]); // Added isLightboxOpen dependency

    // Navigate to previous image
    const goToPrevious = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    };

    // Navigate to next image
    const goToNext = () => {
        setCurrentIndex((prevIndex) => 
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    };

    // Navigate to specific image
    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    // Open lightbox
    const openLightbox = () => {
        setIsLightboxOpen(true);
    };

    // Close lightbox
    const closeLightbox = () => {
        setIsLightboxOpen(false);
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isLightboxOpen) {
                if (e.key === 'Escape') {
                    closeLightbox();
                } else if (e.key === 'ArrowLeft') {
                    goToPrevious();
                } else if (e.key === 'ArrowRight') {
                    goToNext();
                }
            } else {
                if (e.key === 'ArrowLeft') {
                    goToPrevious();
                } else if (e.key === 'ArrowRight') {
                    goToNext();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isLightboxOpen]);

    return (
        <>
            <div className="carousel-container">
                <div className="carousel-wrapper">
                    {/* Left Arrow */}
                    <button 
                        className="carousel-arrow carousel-arrow-left" 
                        onClick={goToPrevious}
                        aria-label="Previous image"
                    >
                        &#8249;
                    </button>

                    {/* Image Display */}
                    <div className="carousel-image-container">
                        {images.map((image, index) => (
                            <div
                                key={index}
                                className={`carousel-slide ${
                                    index === currentIndex ? 'active' : ''
                                }`}
                            >
                                <img 
                                    src={image.src} 
                                    alt={image.alt || `Slide ${index + 1}`}
                                    className="carousel-image"
                                    onClick={openLightbox}
                                    style={{ cursor: 'pointer' }}
                                />
                                {image.caption && (
                                    <div className="carousel-caption">
                                        {image.caption}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Right Arrow */}
                    <button 
                        className="carousel-arrow carousel-arrow-right" 
                        onClick={goToNext}
                        aria-label="Next image"
                    >
                        &#8250;
                    </button>
                </div>
            </div>

            {/* Lightbox Overlay */}
            {isLightboxOpen && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        {/* Close Button */}
                        <button 
                            className="lightbox-close" 
                            onClick={closeLightbox}
                            aria-label="Close lightbox"
                        >
                            &times;
                        </button>

                        {/* Left Arrow */}
                        <button 
                            className="lightbox-arrow lightbox-arrow-left" 
                            onClick={goToPrevious}
                            aria-label="Previous image"
                        >
                            &#8249;
                        </button>

                        {/* Large Image */}
                        <img 
                            src={images[currentIndex].src} 
                            alt={images[currentIndex].alt || `Slide ${currentIndex + 1}`}
                            className="lightbox-image"
                        />

                        {/* Right Arrow */}
                        <button 
                            className="lightbox-arrow lightbox-arrow-right" 
                            onClick={goToNext}
                            aria-label="Next image"
                        >
                            &#8250;
                        </button>

                        {/* Image Counter */}
                        <div className="lightbox-counter">
                            {currentIndex + 1} / {images.length}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Carousel;